import { useState } from "react";
import type { WaveCoverKeys, WaveGallerySection, WaveRecord } from "@vida/wave-core";
import { WaveTemplateCatalog, type WaveTemplate } from "@vida/wave-core";
import { WaveFavoriteButton } from "../shell/WaveBrandHeader";
import { WaveCoverThumbnail } from "./WaveCoverThumbnail";
import { loadGalleryLayout, saveGalleryLayout } from "../../services/waveGalleryLayout";

export type WaveGalleryLayout = "grid" | "list";

type Props = {
  title: string;
  waves?: WaveRecord[];
  sections?: WaveGallerySection[];
  onSelect: (wave: WaveRecord) => void;
  onCreate?: () => void;
  onCreateFromTemplate?: (template: WaveTemplate) => void;
  onCoverUpdated?: (waveId: string, keys: WaveCoverKeys) => void;
  isFavorite?: (waveId: string) => boolean;
  onToggleFavorite?: (waveId: string) => void;
};

function waveSubtitle(wave: WaveRecord): string {
  if (wave.participants.length > 1) {
    return `${wave.participants.length} partecipanti · ${wave.status}`;
  }
  return wave.status;
}

function WaveGalleryCards({
  waves,
  layout,
  onSelect,
  onCoverUpdated,
  idPrefix,
  isFavorite,
  onToggleFavorite,
}: {
  waves: WaveRecord[];
  layout: WaveGalleryLayout;
  onSelect: (wave: WaveRecord) => void;
  onCoverUpdated?: (waveId: string, keys: WaveCoverKeys) => void;
  idPrefix?: string;
  isFavorite?: (waveId: string) => boolean;
  onToggleFavorite?: (waveId: string) => void;
}) {
  if (layout === "grid") {
    return (
      <div className="wave-gallery-grid">
        {waves.map((wave) => (
          <div
            key={idPrefix ? `${idPrefix}-${wave.waveId}` : wave.waveId}
            className="wave-gallery-card-wrap"
          >
            {onToggleFavorite && (
              <WaveFavoriteButton
                className="wave-gallery-card-favorite"
                active={isFavorite?.(wave.waveId) ?? false}
                label={
                  isFavorite?.(wave.waveId)
                    ? "Rimuovi dai preferiti"
                    : "Aggiungi ai preferiti"
                }
                onToggle={() => onToggleFavorite(wave.waveId)}
              />
            )}
            <button
              type="button"
              className="wave-gallery-card"
              onClick={() => onSelect(wave)}
            >
              <WaveCoverThumbnail
                wave={wave}
                variant="card"
                onCoverKeyChange={onCoverUpdated}
              />
              <div className="wave-gallery-card-body">
                <strong>{wave.title}</strong>
                <small>{waveSubtitle(wave)}</small>
              </div>
            </button>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="wave-gallery-list">
      {waves.map((wave) => (
        <div
          key={idPrefix ? `${idPrefix}-${wave.waveId}` : wave.waveId}
          className="wave-gallery-card-wrap wave-gallery-list-wrap"
        >
          <button
            type="button"
            className="wave-gallery-list-row"
            onClick={() => onSelect(wave)}
          >
            <WaveCoverThumbnail
              wave={wave}
              variant="list"
              onCoverKeyChange={onCoverUpdated}
            />
            <div className="wave-gallery-list-text">
              <strong>{wave.title}</strong>
              <small>{waveSubtitle(wave)}</small>
            </div>
            {onToggleFavorite && (
              <WaveFavoriteButton
                active={isFavorite?.(wave.waveId) ?? false}
                label={
                  isFavorite?.(wave.waveId)
                    ? "Rimuovi dai preferiti"
                    : "Aggiungi ai preferiti"
                }
                onToggle={() => onToggleFavorite(wave.waveId)}
              />
            )}
            <span className="wave-gallery-chevron" aria-hidden>
              ›
            </span>
          </button>
        </div>
      ))}
    </div>
  );
}

export function WaveThreadGallery({
  title,
  waves = [],
  sections,
  onSelect,
  onCreate,
  onCreateFromTemplate,
  onCoverUpdated,
  isFavorite,
  onToggleFavorite,
}: Props) {
  const [layout, setLayout] = useState<WaveGalleryLayout>(loadGalleryLayout);
  const showsCreate = Boolean(onCreate || onCreateFromTemplate);
  const hasVisibleContent = sections
    ? sections.some((section) => section.waves.length > 0)
    : waves.length > 0;

  const setLayoutPersisted = (next: WaveGalleryLayout) => {
    setLayout(next);
    saveGalleryLayout(next);
  };

  return (
    <div className="wave-thread-gallery">
      <header className="wave-gallery-header">
        <h1>{title}</h1>
        <div className="wave-gallery-header-actions">
          {showsCreate && (
            <div className="wave-gallery-create-group">
              {onCreate && (
                <button type="button" className="primary-btn" onClick={onCreate}>
                  Nuova wave
                </button>
              )}
              {onCreateFromTemplate && (
                <details className="wave-gallery-template-menu">
                  <summary>Da template</summary>
                  <div className="wave-gallery-template-panel">
                    {WaveTemplateCatalog.all.map((template) => (
                      <button
                        key={template.templateId}
                        type="button"
                        onClick={() => onCreateFromTemplate(template)}
                      >
                        {template.title}
                      </button>
                    ))}
                  </div>
                </details>
              )}
            </div>
          )}
          <div className="wave-gallery-layout-toggle" role="group" aria-label="Layout">
            <button
              type="button"
              className={layout === "grid" ? "active" : ""}
              onClick={() => setLayoutPersisted("grid")}
            >
              Griglia
            </button>
            <button
              type="button"
              className={layout === "list" ? "active" : ""}
              onClick={() => setLayoutPersisted("list")}
            >
              Lista
            </button>
          </div>
        </div>
      </header>

      {!hasVisibleContent ? (
        <div className="empty-state wave-gallery-empty">
          <p>Nessuna wave recente</p>
          <p className="muted">Apri, crea o condividi una wave per vederla qui.</p>
          {onCreate && (
            <button type="button" className="primary-btn" onClick={onCreate}>
              Nuova wave
            </button>
          )}
        </div>
      ) : sections ? (
        <div className="wave-gallery-sections">
          {sections.map((section) =>
            section.waves.length > 0 ? (
              <section key={section.id} className="wave-gallery-section">
                <h2 className="wave-gallery-section-title">{section.title}</h2>
                <WaveGalleryCards
                  waves={section.waves}
                  layout={layout}
                  onSelect={onSelect}
                  onCoverUpdated={onCoverUpdated}
                  idPrefix={section.id}
                  isFavorite={isFavorite}
                  onToggleFavorite={onToggleFavorite}
                />
              </section>
            ) : null,
          )}
        </div>
      ) : (
        <WaveGalleryCards
          waves={waves}
          layout={layout}
          onSelect={onSelect}
          onCoverUpdated={onCoverUpdated}
          isFavorite={isFavorite}
          onToggleFavorite={onToggleFavorite}
        />
      )}
    </div>
  );
}
